/**
 * Bookmark overrides cache subscription.
 *
 * The bookmark overrides slot is a client-side key/value store written by
 * `useToggleBookmark` to propagate optimistic `is_bookmarked` / `in_watchlist`
 * flags across every component that renders the same video — independent of
 * which list cache the video was sourced from. It has no server fetcher, so
 * we subscribe to the query cache directly via `useSyncExternalStore` instead
 * of abusing `useQuery`.
 */

import { hashKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { queryKeys } from '../query-keys';
import type { Video } from '@/types';

const EMPTY_OVERRIDES: Readonly<Record<string, boolean>> = Object.freeze({});

export function useBookmarkOverrides(): Record<string, boolean> {
  const queryClient = useQueryClient();

  const queryKey = queryKeys.videos.bookmarkOverrides();
  const hash = useMemo(() => hashKey(queryKey), [queryKey]);

  const subscribe = useCallback(
    (listener: () => void) => {
      const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
        if (event.query.queryHash === hash) {
          listener();
        }
      });
      return unsubscribe;
    },
    [queryClient, hash]
  );

  const getSnapshot = useCallback(
    () =>
      queryClient.getQueryData<Record<string, boolean>>(queryKey) ??
      (EMPTY_OVERRIDES as Record<string, boolean>),
    [queryClient, queryKey]
  );

  const getServerSnapshot = useCallback(() => EMPTY_OVERRIDES as Record<string, boolean>, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Merge a bookmark override (if any) into a video object. Returns the same
 * reference when no override applies, so memoized consumers stay cheap.
 */
export function applyBookmarkOverride(video: Video, overrides: Record<string, boolean>): Video {
  const override = overrides[video.uuid];
  if (override === undefined) return video;
  if (video.is_bookmarked === override && video.in_watchlist === override) {
    return video;
  }
  return { ...video, is_bookmarked: override, in_watchlist: override };
}
